import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { api } from '../api/client';
import { PRACTICE_TYPE_LABELS, type ExperienceLevel, type Gender, type OnboardingIntent, type Practice } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { SkipLink } from '../components/a11y';
import { GenderPicker } from '../components/GenderPicker';
import { MainPracticePicker } from '../components/MainPracticePicker';
import { TraditionPicker } from '../components/TraditionPicker';
import { useUser } from '../state/UserContext';
import { TRADITION_LABELS, type Tradition } from '../theme/tokens';

type Step = 1 | 2 | 3;
type Scope = 'explore' | 'choose';

const TOTAL_STEPS = 3;
const INTENTS: (OnboardingIntent | null)[] = ['deepen', 'explore', 'routine', null];
const TIME_OPTIONS: (number | null)[] = [3, 5, 10, 20, null];
const EXPERIENCE: (ExperienceLevel | null)[] = ['new', 'some', 'experienced', null];
const MAX_SUGGESTIONS = 3;

/** Up to three short first practices the caller can actually open right now: level 1 (or a
 * standalone practice), fitting the time budget first, then in the caller's tradition order, then
 * shortest -- one per tradition where possible. Deterministic for the same inputs. */
function pickFirstPractices(practices: Practice[], traditionOrder: Tradition[], budgetMinutes: number | null): Practice[] {
  const unique = Array.from(new Map(practices.map((p) => [p.practiceId, p])).values());
  const rank = (t: Tradition) => {
    const i = traditionOrder.indexOf(t);
    return i === -1 ? traditionOrder.length : i;
  };
  const fits = (p: Practice) => budgetMinutes == null || p.durationSeconds <= budgetMinutes * 60;
  const pool = unique
    .filter((p) => p.access === 'available' && (p.level == null || p.level === 1))
    .sort(
      (a, b) =>
        Number(fits(b)) - Number(fits(a)) ||
        rank(a.tradition) - rank(b.tradition) ||
        a.durationSeconds - b.durationSeconds ||
        a.practiceId.localeCompare(b.practiceId),
    );
  const picked: Practice[] = [];
  const seenTraditions = new Set<string>();
  for (const p of pool) {
    if (picked.length >= MAX_SUGGESTIONS) break;
    if (seenTraditions.has(p.tradition)) continue;
    seenTraditions.add(p.tradition);
    picked.push(p);
  }
  for (const p of pool) {
    if (picked.length >= MAX_SUGGESTIONS) break;
    if (!picked.includes(p)) picked.push(p);
  }
  return picked;
}

/**
 * Review fix F4 + the review's onboarding direction: intent and time first, then one or more
 * traditions -- or an explicit "explore all" -- then a first practice. Denominations are
 * expandable; the guide is optional, deferrable and framed as a companion, not the user's
 * identity. Nothing is saved until the end, and finishing stamps `onboardingCompletedAt` via
 * `completeOnboarding: true`, so an empty traditions list reaches Today and stays there.
 */
export function Onboarding() {
  const { me, updateMe } = useUser();
  const { logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const headingRef = useRef<HTMLHeadingElement>(null);

  const [step, setStep] = useState<Step>(1);
  const [intent, setIntent] = useState<OnboardingIntent | null>(me?.intent ?? null);
  const [timeBudget, setTimeBudget] = useState<number | null>(me?.timeBudgetMinutes ?? null);
  const [experience, setExperience] = useState<ExperienceLevel | null>(me?.experienceLevel ?? null);
  const [scope, setScope] = useState<Scope | null>(
    me && me.traditions.length > 0 ? 'choose' : me?.onboardingComplete ? 'explore' : null,
  );
  const [selected, setSelected] = useState<Tradition[]>(me?.traditions ?? []);
  const [primary, setPrimary] = useState<Tradition | null>(me?.primaryTradition ?? null);
  const [gender, setGender] = useState<Gender | null>(me?.gender ?? null);
  const [showGuide, setShowGuide] = useState(false);
  const [suggestions, setSuggestions] = useState<Practice[] | null>(null);
  const [suggestionsFailed, setSuggestionsFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stepMessage, setStepMessage] = useState<string | null>(null);

  const orderedTraditions = useMemo<Tradition[]>(() => {
    if (scope !== 'choose') return [];
    const lead = primary && selected.includes(primary) ? [primary] : [];
    return [...lead, ...selected.filter((tr) => tr !== primary)];
  }, [scope, selected, primary]);

  const effectivePrimary: Tradition | null =
    scope !== 'choose' ? null : selected.length === 1 ? selected[0] : primary && selected.includes(primary) ? primary : null;

  // Move focus to each new step's heading so keyboard and screen-reader users start at the top.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    headingRef.current?.focus();
  }, [step]);

  // Step 3: suggest a first practice. Nothing has been saved yet, so ask per chosen tradition
  // (the server's parent-fallback covers denominations) or, when exploring, for everything.
  useEffect(() => {
    if (step !== 3) return;
    let cancelled = false;
    setSuggestions(null);
    setSuggestionsFailed(false);
    const load =
      orderedTraditions.length > 0
        ? Promise.all(orderedTraditions.slice(0, 3).map((tr) => api.getPractices({ tradition: tr }))).then((rs) =>
            rs.flatMap((r) => r.practices),
          )
        : api.getPractices({}).then((r) => r.practices);
    load
      .then((practices) => {
        if (!cancelled) setSuggestions(pickFirstPractices(practices, orderedTraditions, timeBudget));
      })
      .catch(() => {
        if (!cancelled) {
          setSuggestions([]);
          setSuggestionsFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [step, orderedTraditions, timeBudget]);

  const goToStep = (next: Step) => {
    setStepMessage(null);
    setError(null);
    setStep(next);
  };

  const continueFromTraditions = () => {
    if (scope === 'explore' || (scope === 'choose' && selected.length > 0)) {
      goToStep(3);
    } else {
      setStepMessage(t('onboarding.traditions.needOne'));
    }
  };

  const finish = async (practiceId: string | null) => {
    setSaving(true);
    setError(null);
    try {
      await updateMe({
        intent,
        experienceLevel: experience,
        timeBudgetMinutes: timeBudget,
        traditions: scope === 'choose' ? selected : [],
        primaryTradition: effectivePrimary,
        gender,
        completeOnboarding: true,
      });
      navigate(practiceId ? `/practice/${practiceId}` : '/home', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('onboarding.error'));
      setSaving(false);
    }
  };

  const minutesLabel = (seconds: number) => t('onboarding.first.minutes', { count: Math.max(1, Math.round(seconds / 60)) });

  return (
    <div className="onboarding-shell">
      <SkipLink />
      <header className="onboarding-header">
        <span className="top-bar-brand">
          <span className="logo-badge">
            <img src="/logo-face.png" alt="" className="logo-face" />
          </span>
          <span className="wordmark">Omnist</span>
        </span>
        <button type="button" className="btn btn-ghost btn-small" onClick={logout}>
          {t('onboarding.logout')}
        </button>
      </header>

      <main id="main-content" className="onboarding-main" tabIndex={-1}>
        <div className="onboarding-progress">
          <p className="onboarding-step-label">{t('onboarding.stepOf', { step, total: TOTAL_STEPS })}</p>
          <ol className="onboarding-progress-bar" aria-hidden="true">
            {[1, 2, 3].map((n) => <li key={n} className={n <= step ? 'done' : undefined} />)}
          </ol>
        </div>

        {step === 1 && (
          <section aria-labelledby="onboarding-heading">
            <h1 id="onboarding-heading" ref={headingRef} tabIndex={-1} className="page-title">
              {t('onboarding.intent.title')}
            </h1>
            <p className="page-subtitle">{t('onboarding.intent.subtitle')}</p>

            <fieldset className="choice-group">
              <legend className="choice-legend">{t('onboarding.intent.legend')}</legend>
              <div className="choice-cards">
                {INTENTS.map((value) => (
                  <label key={value ?? 'none'} className={`choice-card${intent === value ? ' selected' : ''}`}>
                    <input type="radio" name="intent" checked={intent === value} onChange={() => setIntent(value)} />
                    <span className="choice-mark" aria-hidden="true" />
                    <span>{t(`onboarding.intent.options.${value ?? 'none'}`)}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="choice-group">
              <legend className="choice-legend">{t('onboarding.intent.timeLegend')}</legend>
              <div className="choice-chips">
                {TIME_OPTIONS.map((value) => (
                  <label key={value ?? 'varies'} className={`choice-chip${timeBudget === value ? ' selected' : ''}`}>
                    <input type="radio" name="time" checked={timeBudget === value} onChange={() => setTimeBudget(value)} />
                    <span>{value == null ? t('onboarding.intent.timeVaries') : t('onboarding.intent.minutes', { count: value })}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="choice-group">
              <legend className="choice-legend">{t('onboarding.intent.experienceLegend')}</legend>
              <div className="choice-chips">
                {EXPERIENCE.map((value) => (
                  <label key={value ?? 'none'} className={`choice-chip${experience === value ? ' selected' : ''}`}>
                    <input type="radio" name="experience" checked={experience === value} onChange={() => setExperience(value)} />
                    <span>{t(`onboarding.intent.experience.${value ?? 'none'}`)}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="onboarding-actions">
              <button type="button" className="btn btn-primary" onClick={() => goToStep(2)}>
                {t('onboarding.continue')}
              </button>
            </div>
          </section>
        )}

        {step === 2 && (
          <section aria-labelledby="onboarding-heading">
            <h1 id="onboarding-heading" ref={headingRef} tabIndex={-1} className="page-title">
              {t('onboarding.traditions.title')}
            </h1>
            <p className="page-subtitle">{t('onboarding.traditions.subtitle')}</p>

            <fieldset className="choice-group">
              <legend className="visually-hidden">{t('onboarding.traditions.scopeLegend')}</legend>
              <div className="choice-cards two">
                {(['explore', 'choose'] as Scope[]).map((value) => (
                  <label key={value} className={`choice-card${scope === value ? ' selected' : ''}`}>
                    <input
                      type="radio"
                      name="scope"
                      checked={scope === value}
                      onChange={() => {
                        setScope(value);
                        setStepMessage(null);
                      }}
                    />
                    <span className="choice-mark" aria-hidden="true" />
                    <span>
                      {t(`onboarding.traditions.${value}.label`)}
                      <span className="choice-hint">{t(`onboarding.traditions.${value}.hint`)}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            {scope === 'choose' && (
              <>
                <TraditionPicker
                  selected={selected}
                  onChange={(next) => {
                    setSelected(next);
                    setStepMessage(null);
                  }}
                />
                {selected.length > 1 && (
                  <>
                    <h2 className="section-title">{t('onboarding.traditions.mainTitle')}</h2>
                    <p className="page-subtitle" style={{ marginBottom: 12 }}>{t('onboarding.traditions.mainHint')}</p>
                    <MainPracticePicker options={selected} selected={primary} onChange={setPrimary} />
                  </>
                )}
              </>
            )}

            <div role="alert" className="onboarding-alert">
              {stepMessage && <p className="error-text">{stepMessage}</p>}
            </div>

            <div className="onboarding-actions">
              <button type="button" className="btn btn-ghost" onClick={() => goToStep(1)}>
                {t('onboarding.back')}
              </button>
              <button type="button" className="btn btn-primary" onClick={continueFromTraditions}>
                {t('onboarding.continue')}
              </button>
            </div>
          </section>
        )}

        {step === 3 && (
          <section aria-labelledby="onboarding-heading">
            <h1 id="onboarding-heading" ref={headingRef} tabIndex={-1} className="page-title">
              {t('onboarding.first.title')}
            </h1>
            <p className="page-subtitle">{t('onboarding.first.subtitle')}</p>

            {suggestions === null ? (
              <p className="page-subtitle" role="status">{t('onboarding.first.loading')}</p>
            ) : suggestions.length === 0 ? (
              <p className="page-subtitle" role="status">
                {suggestionsFailed ? t('onboarding.first.error') : t('onboarding.first.none')}
              </p>
            ) : (
              <ul className="first-practice-list">
                {suggestions.map((p) => (
                  <li key={p.practiceId} className="first-practice-card">
                    <div>
                      <h2 className="first-practice-title">{p.title}</h2>
                      <p className="practice-item-meta">
                        {TRADITION_LABELS[p.tradition] ?? p.tradition} &middot; {PRACTICE_TYPE_LABELS[p.type]} &middot;{' '}
                        {minutesLabel(p.durationSeconds)}
                      </p>
                      <p className="practice-item-meta">
                        {p.reviewerStatus === 'reviewed' && p.reviewerName
                          ? t('reviewStatus.reviewed', { name: p.reviewerName })
                          : t('reviewStatus.draft')}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={saving}
                      onClick={() => finish(p.practiceId)}
                      aria-label={`${t('onboarding.first.begin')}: ${p.title}`}
                    >
                      {t('onboarding.first.begin')}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="onboarding-guide">
              <button
                type="button"
                className="btn btn-ghost"
                aria-expanded={showGuide}
                aria-controls="onboarding-guide-panel"
                onClick={() => setShowGuide((s) => !s)}
              >
                {showGuide ? t('onboarding.first.guide.hide') : t('onboarding.first.guide.toggle')}
              </button>
              <div id="onboarding-guide-panel" className="onboarding-guide-panel" hidden={!showGuide}>
                <p className="page-subtitle">{t('onboarding.first.guide.body')}</p>
                <GenderPicker selected={gender} onChange={setGender} tradition={effectivePrimary} />
                <button
                  type="button"
                  className={`btn btn-ghost btn-small${gender === null ? ' is-selected' : ''}`}
                  aria-pressed={gender === null}
                  onClick={() => setGender(null)}
                >
                  {t('onboarding.first.guide.noPreference')}
                </button>
              </div>
            </div>

            <div role="alert" className="onboarding-alert">
              {error && <p className="error-text">{error}</p>}
            </div>

            <div className="onboarding-actions">
              <button type="button" className="btn btn-ghost" onClick={() => goToStep(2)} disabled={saving}>
                {t('onboarding.back')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => finish(null)} disabled={saving}>
                {saving ? t('onboarding.saving') : t('onboarding.first.goToday')}
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
